import React, { useState, useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';
import './EditCompanyProfileModal.css'; // Premium dedicated stylesheet
import api from '../../../services/api';

// Maximum characters allowed for a single capability.
const CAPABILITY_MAX_LENGTH = 100;

const EditCompanyProfileModal = ({ profile, onClose, onSave }) => {
    const [companyName, setCompanyName] = useState(
        profile?.company_name || "EDGE"
    );
    const [description, setDescription] = useState(
        profile?.description ||
        "EDGE is an advanced technology group specializing in digital transformation, defence technologies, enterprise software development, and artificial intelligence solutions. Headquartered in the region, the company develops cutting-edge products across cyber technologies, automation, and advanced systems integration.\n\nWith a strong focus on innovation and strategic collaboration, EDGE delivers next-generation software platforms that enhance operational efficiency, security, and digital capabilities for both enterprise and government sectors."
    );
    const [capabilities, setCapabilities] = useState(
        profile?.capabilities && profile.capabilities.length > 0 ? profile.capabilities : [
            "Cloud Engineering & Infrastructure Automation",
            "Enterprise Software Development & SaaS Platforms",
            "Artificial Intelligence & Intelligent Agents",
            "Cybersecurity & Information Security Solutions",
            "Data Analytics & Business Intelligence",
            "Advanced Technology & Digital Transformation Advisory"
        ]
    );
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState('');

    useEffect(() => {
        // Lock body scrolling when modal opens
        document.body.style.overflow = 'hidden';
        return () => {
            // Restore body scrolling on unmount
            document.body.style.overflow = '';
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setApiError('');

        // Strict Client-side Validation
        if (!companyName.trim()) {
            setApiError('Company Name is required.');
            setSubmitting(false);
            return;
        }
        if (!description.trim()) {
            setApiError('Description is required.');
            setSubmitting(false);
            return;
        }

        // Strict Sanitization & Deduplication
        const sanitizedCapabilities = Array.from(
            new Set(capabilities.map(c => c.trim()).filter(Boolean))
        );

        const payload = {
            company_name: companyName.trim(),
            description: description.trim(),
            capabilities: sanitizedCapabilities
        };

        try {
            console.log('[API REQUEST] PUT /company/profile Payload:', JSON.stringify(payload, null, 2));
            const response = await api.put('/company/profile', payload);
            if (response.status === 200 || response.status === 201) {
                // Return the updated profile from response, keeping format consistent
                onSave(response.data && response.data.company_name ? response.data : { ...payload, profile_id: profile?.profile_id || 'default' });
            } else {
                setApiError(response.data?.detail || 'Failed to update company profile.');
            }
        } catch (err) {
            console.error('Update profile error:', err);
            let errorMsg = 'Failed to update company profile.';
            if (err.response) {
                if (err.response.status === 422) {
                    const detail = err.response.data?.detail;
                    if (Array.isArray(detail)) {
                        errorMsg = `Validation Error: ${detail.map(d => `${d.loc.filter(l => l !== 'body').join('.')} (${d.msg})`).join(', ')}`;
                    } else {
                        errorMsg = detail || 'Validation failed on the server.';
                    }
                } else {
                    errorMsg = err.response?.data?.detail || err.response?.data?.message || errorMsg;
                }
            } else if (err.request) {
                errorMsg = 'No response received from the server. Please check your network connection.';
            } else {
                errorMsg = err.message || errorMsg;
            }
            setApiError(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="company-profile-overlay" onClick={onClose}>
            <div className="company-profile-box" onClick={(e) => e.stopPropagation()}>
                <div className="company-profile-header">
                    <span className="company-profile-title">Edit Company Profile</span>
                    <button type="button" className="company-profile-close-btn" onClick={onClose} title="Close">
                        <X size={20} />
                    </button>
                </div>

                <form className="company-profile-form" onSubmit={handleSubmit}>
                    <div className="company-profile-body">
                        <div className="company-profile-section">
                            <label className="company-profile-label">Company Name</label>
                            <input
                                type="text"
                                className="company-profile-input"
                                name="companyName"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder="Enter company name..."
                                autoFocus
                                required
                            />
                        </div>

                        <div className="company-profile-section">
                            <label className="company-profile-label">Description</label>
                            <textarea
                                className="company-profile-textarea"
                                name="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                            />
                        </div>

                        <div className="company-profile-section">
                            <div className="company-profile-section-header">
                                <label className="company-profile-label">Capabilities</label>
                                <span className="company-profile-helper">Click any capability to edit</span>
                            </div>
                            <div className="company-capabilities-grid">
                                {capabilities.map((cap, idx) => (
                                    <div key={idx} className="company-capability-card">
                                        <div className="company-capability-dot" />
                                        <div className="company-capability-input-wrapper">
                                            <textarea
                                                value={cap}
                                                onChange={(e) => {
                                                    const updated = [...capabilities];
                                                    updated[idx] = e.target.value;
                                                    setCapabilities(updated);
                                                }}
                                                placeholder="Enter capability..."
                                                rows={2}
                                                maxLength={CAPABILITY_MAX_LENGTH}
                                                className="company-capability-textarea"
                                            />
                                            <span
                                                className={`company-capability-counter${cap.length >= CAPABILITY_MAX_LENGTH ? ' at-limit' : ''}`}
                                                aria-live="polite"
                                            >
                                                {cap.length}/{CAPABILITY_MAX_LENGTH}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const updated = capabilities.filter((_, i) => i !== idx);
                                                setCapabilities(updated);
                                            }}
                                            className="company-capability-delete-btn"
                                            title="Delete capability"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={() => setCapabilities([...capabilities, ''])}
                                className="company-add-capability-btn"
                            >
                                <span>+ Add Capability</span>
                            </button>
                        </div>

                        {apiError && (
                            <div className="company-profile-api-error">{apiError}</div>
                        )}
                    </div>

                    <div className="company-profile-footer">
                        <button
                            type="button"
                            className="company-profile-btn-cancel"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="company-profile-btn-save"
                            disabled={submitting}
                        >
                            {submitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditCompanyProfileModal;
